<template>
  <div
    class="min-h-screen flex flex-col justify-center items-center px-4 py-12 sm:px-6 lg:px-8 overflow-hidden relative bg-white dark:bg-black text-gray-900 dark:text-gray-100"
  >
    <!-- Theme toggle button in top right corner -->
    <button
      class="absolute top-4 right-4 z-20 p-2 rounded-full transition-colors duration-200 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
      @click="theme.toggleTheme"
    >
      <!-- Sun icon for dark mode (switch to light) -->
      <svg
        v-if="theme.isDark.value"
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-yellow-300"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
      <!-- Moon icon for light mode (switch to dark) -->
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-gray-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
        />
      </svg>
    </button>

    <!-- Login container -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md z-10">
      <div class="mx-auto flex items-center justify-center mb-6">
        <div class="flex items-center p-2">
          <!-- Wrench icon - Matching sidemenu logo but larger -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-16 w-16 mr-2"
            viewBox="0 0 24 24"
          >
            <path
              d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"
              fill="#FFD700"
              stroke="#FFD700"
              stroke-width="0.5"
            />
          </svg>

          <!-- Text logo - Larger -->
          <div class="flex items-center">
            <span class="text-5xl font-bold text-[#1E90FF] drop-shadow-sm">dev</span>
            <span class="text-5xl font-bold text-[#32CD32] drop-shadow-sm">tools</span>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 sm:mx-auto sm:w-full sm:max-w-md z-10">
      <div
        class="backdrop-blur-lg py-8 px-6 rounded-2xl bg-white/40 dark:bg-gray-800/40 shadow-lg dark:shadow-gray-900/50"
      >
        <div
          v-if="error"
          class="mb-5 bg-red-50 border-l-4 border-red-400 p-4 rounded-md animate-fadeIn"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">
                {{ error }}
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <!-- Authentik Sign In Button -->
          <div class="flex flex-col items-center">
            <button
              class="w-full flex items-center justify-center px-6 py-4 border border-transparent rounded-xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transform transition-all duration-150 ease-in-out hover:scale-[1.02] active:scale-[0.98]"
              :disabled="loading"
              @click="initiateAuthentikSignIn"
            >
              <!-- Authentik Logo/Icon -->
              <svg
                class="w-6 h-6 mr-3"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                />
              </svg>
              <span class="text-lg">Sign in with Authentik</span>
            </button>
          </div>

          <!-- Loading spinner -->
          <div
            v-if="loading"
            class="mt-5 flex justify-center"
          >
            <svg
              class="animate-spin h-8 w-8 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-8 text-center text-xs text-gray-600 dark:text-gray-400">
        &copy; {{ new Date().getFullYear() }} Platform Team
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  triggerAuthentikSignIn,
  handleAuthentikCallback as processAuthentikCallback,
  loadAuthentikAuth
} from '../services/auth/authentikAuth'
import authConfig from '../config/authConfig'
import auth from '../services/auth/auth'
import { useTheme } from '../utils/useTheme'
import { logger } from '../utils/logger'

export default defineComponent({
  name: 'Login',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const error = ref('')
    const loading = ref(false)
    const theme = inject('theme', useTheme())

    // Configure Authentik
    ;(window as any).authentikClientId = authConfig.authentikClientId || ''
    ;(window as any).authentikAuthorizationEndpoint =
      authConfig.authentikAuthorizationEndpoint || ''
    ;(window as any).authentikTokenEndpoint = authConfig.authentikTokenEndpoint || ''
    ;(window as any).authentikUserInfoEndpoint = authConfig.authentikUserInfoEndpoint || ''
    ;(window as any).authentikLogoutEndpoint = authConfig.authentikLogoutEndpoint || ''
    ;(window as any).authentikScope = authConfig.authentikScope || ''

    // Log Authentik configuration
    logger.log('Authentik configuration loaded:', {
      clientId: authConfig.authentikClientId,
      hasAuthEndpoint: !!authConfig.authentikAuthorizationEndpoint,
      hasTokenEndpoint: !!authConfig.authentikTokenEndpoint,
      hasUserInfoEndpoint: !!authConfig.authentikUserInfoEndpoint,
      hasLogoutEndpoint: !!authConfig.authentikLogoutEndpoint,
      hasScope: !!authConfig.authentikScope
    })

    const initiateAuthentikSignIn = async () => {
      loading.value = true
      error.value = ''

      try {
        await triggerAuthentikSignIn()
      } catch (err) {
        logger.error('Authentik Sign-In error:', err)
        error.value = 'Unable to sign in with Authentik. Please try again later.'
        loading.value = false
      }
    }

    const handleAuthentikCallback = async (code: string, state: string) => {
      loading.value = true
      error.value = ''

      try {
        const response = await processAuthentikCallback(code, state)

        if (!response) {
          throw new Error('No user data received from Authentik')
        }

        // Extract user data from Authentik response
        // response is already an AuthentikUser object
        const user = {
          id: response.id,
          name: response.name,
          email: response.email,
          avatar: '', // Authentik doesn't provide avatar
          token: response.idToken || '',
          groups: response.groups || []
        }

        // Tokens are now stored securely in the auth service
        // No need to store in localStorage here

        // Now call handleAuthentikLoginSuccess with the proper user object
        const authenticatedUser = auth.handleAuthentikLoginSuccess(user)

        // Get redirect URL from session storage or default to home
        const redirectTo = sessionStorage.getItem('authentik_redirect_to') || '/'
        sessionStorage.removeItem('authentik_redirect_to')

        if (authenticatedUser) {
          router.push(redirectTo)
        }
      } catch (err) {
        logger.error('Authentik callback error:', err)
        error.value = 'Unable to complete Authentik sign in. Please try again.'
      } finally {
        loading.value = false
      }
    }

    onMounted(async () => {
      try {
        logger.log('Login component mounted')
        logger.log('Auth config:', authConfig)

        if (!authConfig.authentikClientId) {
          throw new Error('Authentik client ID is not configured')
        }

        // Set Authentik configuration
        ;(window as any).authentikClientId = authConfig.authentikClientId
        ;(window as any).authentikAuthorizationEndpoint = authConfig.authentikAuthorizationEndpoint
        ;(window as any).authentikTokenEndpoint = authConfig.authentikTokenEndpoint
        ;(window as any).authentikUserInfoEndpoint = authConfig.authentikUserInfoEndpoint
        ;(window as any).authentikLogoutEndpoint = authConfig.authentikLogoutEndpoint

        logger.log('Window configuration after setup:', {
          clientId: (window as any).authentikClientId,
          authEndpoint: (window as any).authentikAuthorizationEndpoint,
          tokenEndpoint: (window as any).authentikTokenEndpoint
        })

        // Check for OAuth callback parameters
        const code = route.query.code as string
        const state = route.query.state as string

        if (code && state) {
          // Handle Authentik OAuth callback
          await handleAuthentikCallback(code, state)
          return
        }

        // Check if already authenticated
        if (auth.isAuthenticated()) {
          // Check if there's a redirect query parameter
          const redirectPath = router.currentRoute.value.query.redirect as string
          if (redirectPath) {
            // Redirect to the originally requested URL
            router.push(redirectPath)
          } else {
            // Default redirect to dashboard
            router.push('/')
          }
          return
        }

        // Store redirect URL for after authentication
        const redirectPath = route.query.redirect as string
        if (redirectPath) {
          sessionStorage.setItem('authentik_redirect_to', redirectPath)
        }
      } catch (err: unknown) {
        logger.error('Error initializing auth:', err)
        error.value = 'Unable to load sign in. Please refresh the page and try again.'
      } finally {
        loading.value = false
      }
    })

    return {
      error,
      loading,
      initiateAuthentikSignIn,
      theme
    }
  }
})
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}
</style>
